import { questionTopicRequest } from './qbankNavigation';
import { EXPLORE_ROUTES } from "../homeDashboard/exploreRoutes";
import { QUESTION_BANK_ROUTES } from "./questionBankRoutes";
import React, { useEffect, useMemo, useState } from "react";
import HomeDashboardLayout from "../homeDashboard/HomeDashboardLayout";
import QBankFilter from "./QBankFilter";
import "../dashboard/Dashboard.css";
import "../homeDashboard/HomeDashboard.css";
import "./QuestionBankList.css";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageMobileIcon, BackSVG } 
from "../components/SvgIcons";
import { useQuestionBankQuestions } from "../hooks/useQuestionBankQuestions";
import { useQuestionBankGuessScore } from "../hooks/useQuestionBankGuessScore";
import { useQuestionBankQuestionActions } from "../hooks/useQuestionBankQuestionActions";
import ReportModal from "../components/ReportModal";
import StickyNotesPopup from "../components/StickyNotesPopup";
import ViewModal from "../components/ViewModal";
import GuessScoreModal from "../components/GuessScoreModal";
import { useSidebarData } from "../hooks/useSidebarData";
import {
  resolveQuestionBankStateId,
  resolveQuestionBankStateName,
} from "./stateUtils";
import {
  buildQuestionBankBackTarget,
  buildQuestionBankFlowState,
  buildQuestionBankSubjectsState,
  getStoredQuestionBankFlowState,
  persistQuestionBankFlowState,
} from "./qbankFlowState";
import bookmark from "../assets/images/bookmark.png";
import Swal from 'sweetalert2';

const QUESTION_BANK_CATEGORY_ID = 2;
const DEFAULT_SUBJECT_ID = 1;

function buildVisiblePages(currentPage, totalPages) {
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const halfWindow = Math.floor(maxVisiblePages / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - maxVisiblePages + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

function mapFiltersToApiParams(filters = {}) {
  const mappings = {
    bookmark_status: "bookmark_status",
    report_status: "report_status",
    languages: "language",
    question_level: "question_level",
    question_types: "question_type",
    question_importance: "quest_importance",
    tags: "question_tags",
    text_pic_filter: "text_pic",
    free_paid_filter: "free_paid",
    personal_notes: "personal_notes",
    solved_unsolved: "solved_unsolved",
    correct_incorrect: "correct_incorrect",
    guess_filters: "guess_filter",
  };

  return Object.entries(filters).reduce((accumulator, [key, value]) => {
    if (value === undefined || value === null || value === "") {
      return accumulator;
    }

    accumulator[mappings[key] || key] = value;
    return accumulator;
  }, {});
}

 

const QuickActionCard = () => (
  <div className="qbl-browserCard">
      <div className="qbl-browserCard__copy">
      <div className="qbl-browserCard__icon" aria-hidden="true">
          <i className="bi bi-link"></i>
      </div>
      <div>
        <p className="qbl-browserCard__title">Continue on browser..</p>
        <p className="qbl-browserCard__link">https://qbank.vidyakasam.com/</p>
      </div>
    </div>

    <div className="qbl-browserCard__actions" aria-label="Question bank tools">
      <button type="button" className="qbl-browserCard__action" aria-label="Copy link">
        <i className="bi bi-copy" aria-hidden="true" />
      </button>
      <button type="button" className="qbl-browserCard__action qbl-browserCard__action--primary" aria-label="Share link">
        <i className="bi bi-share-fill" aria-hidden="true" />
      </button>
      <button type="button" className="qbl-browserCard__action" aria-label="Show QR code">
        <i className="bi bi-qr-code-scan" aria-hidden="true" />
      </button>
    </div>
  </div>
);

const QuestionCardShimmer = () => (
  <article className="qbl-questionCard" aria-hidden="true">
    <div className="qbl-questionCard__head">
      <div className="qbl-questionCard__meta">
        <span className="dashboard-skeleton dashboard-skeleton--button qbl-questionCard__shimmer-badge" />
        <span className="dashboard-skeleton dashboard-skeleton--icon qbl-questionCard__shimmer-lock" />
      </div>
      <span className="dashboard-skeleton dashboard-skeleton--icon qbl-questionCard__shimmer-bookmark" />
    </div>
    <span className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-line" />
    <span className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-line" />
    <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short qbl-questionCard__shimmer-line" />
    <div className="qbl-questionCard__footer">
      {Array.from({ length: 4 }).map((_, index) => (
        <span key={index} className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-footer" />
      ))}
    </div>
  </article>
);

const SummaryRowShimmer = () => (
  <div className="qbl-summaryRow" aria-hidden="true">
    <span className="dashboard-skeleton dashboard-skeleton--title qbl-summaryRow__shimmer-title" />
    <span className="dashboard-skeleton dashboard-skeleton--button qbl-summaryRow__shimmer-guess" />
  </div>
);

const QuestionCard = ({
  item,
  onOpen,
  onBookmark,
  onLike,
  onView,
  onReport,
  onNotes,
  isBookmarkUpdating = false,
  isLikeUpdating = false,
}) => (
  <article
    className="qbl-questionCard"
    onClick={onOpen ? () => onOpen(item) : undefined}
    role={onOpen ? "button" : undefined}
    tabIndex={onOpen ? 0 : undefined}
    onKeyDown={
      onOpen
        ? (event) => {
            if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              onOpen(item);
            }
          }
        : undefined
    }
  >
    <div className="qbl-questionCard__head">
      <div className="qbl-questionCard__meta">
        <span className="qbl-questionCard__number">{String(item.serialNo).padStart(2, "0")}</span>
        {item.isLocked ? (
          <span className="qbl-questionCard__lock" aria-hidden="true">
            <i className="bi bi-lock-fill" />
          </span>
        ) : null}
      </div>

      <button
        type="button"
        className="qbl-questionCard__bookmark"
        aria-label="Bookmark question"
        aria-pressed={Boolean(item.isBookmarked)}
        onClick={(event) => {
          event.stopPropagation();
          onBookmark(item);
        }}
        disabled={isBookmarkUpdating}
      >
        {item.isBookmarked ? <img src={bookmark} alt="" /> : <i className="bi bi-bookmark" aria-hidden="true" />}
      </button>
    </div>

    <div
      className="qbl-questionCard__question"
      dangerouslySetInnerHTML={{ __html: item.questionHtml || item.question }}
    />

    <div className="qbl-questionCard__footer">
      <button
        type="button"
        className="qbl-questionCard__footerItem"
        aria-pressed={Boolean(item.isLiked)}
        onClick={(event) => {
          event.stopPropagation();
          onLike(item);
        }}
        disabled={isLikeUpdating}
      >
        <i className={`bi ${item.isLiked ? "bi-heart-fill" : "bi-heart"}`} aria-hidden="true" />
        <span>Like</span>
        <strong>{item.likeCount}</strong>
      </button>

      <button
        type="button"
        className="qbl-questionCard__footerItem"
        onClick={(event) => {
          event.stopPropagation();
          onView(item);
        }}
      >
        <i className="bi bi-eye" aria-hidden="true" />
        <span>View</span>
        <strong>{item.viewCount}</strong>
      </button>

      <button type="button" className="qbl-questionCard__footerItem" onClick={(event) => {
        event.stopPropagation();
        onReport(item);
      }}>
        <i className="bi bi-exclamation-circle" aria-hidden="true" />
        <span>Report</span>
        <strong>{item.reportCount}</strong>
      </button>

      <button type="button" className="qbl-questionCard__footerItem" onClick={(event) => {
        event.stopPropagation();
        onNotes(item);
      }}>
        <i className="bi bi-journal-text" aria-hidden="true" />
        <span>Notes</span>
        <strong>{item.notesCount}</strong>
      </button>
    </div>
  </article>
);

const QuestionBankList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sidebarData = null } = useSidebarData();
  const storedFlowState = useMemo(() => getStoredQuestionBankFlowState(), []);
  const flowState = useMemo(
    () => buildQuestionBankFlowState(location.state, storedFlowState),
    [location.state, storedFlowState]
  );
  const initialListQueryState = flowState?.listQueryState || {};
  const [searchTerm, setSearchTerm] = useState(String(initialListQueryState.searchTerm || ""));
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(String(initialListQueryState.searchTerm || ""));
  const [appliedFilters, setAppliedFilters] = useState(initialListQueryState.appliedFilters || {});
  const [currentPage, setCurrentPage] = useState(Number(initialListQueryState.currentPage || 1));
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [mergedQuestions, setMergedQuestions] = useState([]);
  const [loadedPages, setLoadedPages] = useState(new Set());
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isGuessScoreOpen, setIsGuessScoreOpen] = useState(false);
  const [guessScoreView, setGuessScoreView] = useState("subject");
  const resolvedStateId = useMemo(
    () =>
      resolveQuestionBankStateId({
        locationState: flowState,
        sidebarData,
      }),
    [flowState, sidebarData]
  );
  const hasTopicContext = Number(flowState?.topic_id || 0) > 0;
  const filterQueryParams = useMemo(
    () => ({
      states_id: resolvedStateId,
      category_id: QUESTION_BANK_CATEGORY_ID,
    }),
    [resolvedStateId]
  );
  const questionQueryParams = useMemo(
    () => ({
      ...filterQueryParams,
      subject_id: Number(
        flowState?.selectedSubject?.id ||
          flowState?.subject_id ||
          DEFAULT_SUBJECT_ID
      ),
      ...questionTopicRequest(flowState),
      ...(Number(flowState?.exam_id || 0) > 0 ? { exam_id: Number(flowState.exam_id) } : {}),
      ...(flowState?.free_paid ? { free_paid: flowState.free_paid } : {}),
      ...(flowState?.language ? { language: flowState.language } : {}),
      platform: "web",
      page: currentPage,
      ...(debouncedSearchTerm.trim() ? { search_term: debouncedSearchTerm.trim() } : {}),
      ...mapFiltersToApiParams(appliedFilters),
    }),
    [appliedFilters, currentPage, debouncedSearchTerm, filterQueryParams, flowState]
  );
  const {
    data: questionData,
    isLoading: questionsLoading,
    isFetching: questionsFetching,
    isError: hasQuestionsError,
    error: questionsError,
    refetch: refetchQuestions,
  } = useQuestionBankQuestions(questionQueryParams);
  const guessScoreParams = useMemo(
    () => ({
      subject_id: Number(
        flowState?.selectedSubject?.id ||
          flowState?.subject_id ||
          DEFAULT_SUBJECT_ID
      ),
      state_id: Number(
        resolvedStateId
      ),
      ...(hasTopicContext ? { topic_id: Number(flowState.topic_id) } : {}),
    }),
    [flowState, resolvedStateId, hasTopicContext]
  );
  const guessScoreQuery = useQuestionBankGuessScore(guessScoreParams, {
    enabled: true,
  });
  const allSubjectsGuessScoreQuery = useQuestionBankGuessScore(
    {
      ...guessScoreParams,
      subject_id: 0,
    },
    {
      enabled: true,
    }
  );
  const { toggleBookmark, toggleLike } = useQuestionBankQuestionActions(questionQueryParams);
  const visiblePages = buildVisiblePages(
    Number(questionData?.pagination?.currentPage || currentPage),
    Number(questionData?.pagination?.totalPages || 1)
  );
  const resolvedStateName =
    questionData?.stateName ||
    resolveQuestionBankStateName({
      locationState: flowState,
      requestParams: filterQueryParams,
      sidebarData,
    }) ||
    "State not selected";
  const resolvedSubjectName =
    location.state?.selectedSubtopic?.name ||
    location.state?.selectedTopic?.name ||
    questionData?.subjectName ||
    location.state?.selectedSubject?.title ||
    "General Hindi";
  const guessScoreLabel = guessScoreQuery.data?.score || "0%";
  const guessScoreTitle =
    guessScoreQuery.data?.subjectName ||
    resolvedSubjectName.split(" ").slice(0, -1).join(" ") ||
    resolvedSubjectName;
  const guessScoreSubtitle =
    guessScoreQuery.data?.topicTitle ||
    location.state?.selectedSubtopic?.name ||
    location.state?.selectedTopic?.name ||
    resolvedSubjectName;
  const loadError = hasQuestionsError
    ? questionsError?.response?.data?.message || questionsError?.message || "Unable to load questions."
    : "";
  const isQuestionListPending = questionsLoading || questionsFetching;
  const listScreenState = useMemo(
    () =>
      buildQuestionBankFlowState({
        ...flowState,
        listQueryState: {
          currentPage,
          searchTerm,
          appliedFilters,
        },
        qbankBackTarget:
          flowState?.qbankBackTarget ||
          (flowState?.selectedSubtopic
            ? buildQuestionBankBackTarget(EXPLORE_ROUTES.subtopics, flowState)
            : flowState?.selectedTopic
              ? buildQuestionBankBackTarget(EXPLORE_ROUTES.topics, flowState)
              : buildQuestionBankBackTarget(EXPLORE_ROUTES.subjects, buildQuestionBankSubjectsState(flowState))),
      }),
    [appliedFilters, currentPage, flowState, searchTerm]
  );

  useEffect(() => {
    persistQuestionBankFlowState(listScreenState);
  }, [listScreenState]);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => window.clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
    setMergedQuestions([]);
    setLoadedPages(new Set());
  }, [appliedFilters, debouncedSearchTerm]);

  useEffect(() => {
    const incomingQuestions = questionData?.questions || [];
    if (!incomingQuestions.length) {
      if (Number(questionData?.pagination?.currentPage || currentPage || 1) === 1) {
        setMergedQuestions([]);
      }
      return;
    }

    const fetchedPage = Number(questionData?.pagination?.currentPage || currentPage || 1);
    setLoadedPages((current) => new Set(current).add(fetchedPage));
    setMergedQuestions((current) => {
      const questionById = new Map();
      // Incoming cache updates must replace saved copies, including mutations.
      const orderedQuestions = [...current, ...incomingQuestions];

      orderedQuestions.forEach((question) => {
        const questionId = Number(question?.id || 0);
        if (questionId) {
          questionById.set(questionId, question);
        }
      });

      return Array.from(questionById.values()).sort(
        (first, second) => Number(first?.serialNo || 0) - Number(second?.serialNo || 0)
      );
    });
  }, [currentPage, questionData]);

  useEffect(() => {
    const handleScroll = () => {
      const totalPages = Number(questionData?.pagination?.totalPages || 1);
      const nextPage = Number(questionData?.pagination?.nextPage || 0) || currentPage + 1;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const isNearBottom = scrollTop + viewportHeight >= documentHeight - 240;

      if (
        !isNearBottom ||
        isQuestionListPending ||
        currentPage >= totalPages ||
        loadedPages.has(nextPage)
      ) {
        return;
      }

      setCurrentPage(nextPage);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentPage, isQuestionListPending, loadedPages, questionData?.pagination]);

  useEffect(() => {
    if (!flowState?.refresh_list) {
      return;
    }

    refetchQuestions();
  }, [flowState?.refresh_list, refetchQuestions]);

  useEffect(() => {
    if (!isGuessScoreOpen) {
      setGuessScoreView("subject");
    }
  }, [isGuessScoreOpen]);

  const openFilters = () => {
    setIsFilterOpen(true);
  };

  const closeFilters = () => {
    setIsFilterOpen(false);
  };

  useEffect(() => {
    if (!isFilterOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeFilters();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFilterOpen]);

  const handleApplyFilters = (nextFilters) => {
    setAppliedFilters(nextFilters || {});
    setIsFilterOpen(false);
  };

  const handleBookmarkToggle = async (question) => {
    if (!question?.id || toggleBookmark.isPending) {
      return;
    }

    try {
      const { payload } = await toggleBookmark.mutateAsync({ question_id: Number(question.id) });
      setMergedQuestions(current => current.map(item => Number(item.id) === Number(question.id)
        ? { ...item, isBookmarked: Number(payload.bookmark_status) === 1 } : item));
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Unable to update bookmark', text: error.message });
    }
  };

  const handleLikeToggle = async (question) => {
    if (!question?.id || toggleLike.isPending) {
      return;
    }

    try {
      const { payload } = await toggleLike.mutateAsync({ question_id: Number(question.id) });
      setMergedQuestions(current => current.map(item => Number(item.id) === Number(question.id)
        ? { ...item, isLiked: Number(payload.like_status) === 1,
          likeCount: Number(payload.total_liked ?? payload.total_likes ?? item.likeCount) } : item));
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Unable to update like', text: error.message });
    }
  };

  const handleOpenViewModal = (question) => {
    setSelectedQuestion(question || null);
    setIsViewModalOpen(true);
  };

  const handleOpenReportModal = (question) => {
    setSelectedQuestion(question || null);
    setIsReportModalOpen(true);
  };

  const handleOpenNotesModal = (question) => {
    setSelectedQuestion(question || null);
    setIsNotesModalOpen(true);
  };

  const handleOpenQuestion = (question) => {
    navigate(QUESTION_BANK_ROUTES.practice, {
      state: {
        ...listScreenState,
        question_id: Number(question?.id || 0),
        question_index: Number(question?.question_index || question?.question_number || question?.serialNo || 0),
        selectedQuestion: question || null,
        initial_questions_page: Number(questionData?.pagination?.currentPage || currentPage || 1),
        question_filters: { ...questionQueryParams, page: undefined },
        listQueryState: {
          currentPage,
          searchTerm,
          appliedFilters,
        },
        qbankBackTarget: buildQuestionBankBackTarget(QUESTION_BANK_ROUTES.list, listScreenState),
        initial_questions_payload: questionData
          ? {
              questions: mergedQuestions.length ? mergedQuestions : questionData.questions || [],
              pagination: questionData.pagination || {},
              totalQuestions: Number(questionData.totalResults || mergedQuestions.length || 0),
              stateName: questionData.stateName || "",
              subjectName: questionData.subjectName || "",
              guessScore: questionData.guessScore || "0%",
            } 
          : null,
      },
    });
  };

  const handleBackNavigation = () => {
    if (flowState?.qbankBackTarget?.pathname) {
      navigate(flowState.qbankBackTarget.pathname, {
        replace: true,
        state: flowState.qbankBackTarget.state,
      });
      return;
    }

    navigate(EXPLORE_ROUTES.subjects, {
      replace: true,
      state: buildQuestionBankSubjectsState(flowState),
    });
  };

  return (
      <>
      <style>{`
 .homeDashboard-wrapper .page-content {
  padding-right: 0;
}
      `}</style>
    <HomeDashboardLayout showHeader={false}>
      <div className="qbl-page qbankList-page">
        <div className="qbl-main" aria-label="Question bank content">
          <section className="qbl-panel">
                    <div className="d-flex justify-content-between align-items-center d-xl-none">
                      <button
                        type="button"
                        className="languageImg d-flex d-xl-none doubt-back border-0 bg-transparent p-0"
                        onClick={handleBackNavigation}
                        aria-label="Go back"
                      >
                        <BackSVG />
                      </button>
                      <h4 className="fw-bold mb-0 blackText">Questions</h4>
                      <div className="d-flex gap-2">
                       <div className="languageImg d-flex d-xl-none"> <LanguageMobileIcon/></div> 
                      </div>
                      
                    </div>
            
          <div className="qb-body">
            <div className="d-flex align-items-center justify-content-center">
              <div className="qb-copy">

                <div className="qb-loc">
                  <i className="bi bi-geo-alt-fill" />
                  <span> {resolvedStateName}</span>
                </div>

                {/* Breadcrumb */}
               <div className="hp-breadcrumb">
                  <span className="hp-breadcrumb__item">
                    Himachal Pradesh
                  </span>

                  <span className="hp-breadcrumb__item">
                    Question Bank
                  </span>

                  <span className="hp-breadcrumb__item">
                    Current Affairs
                  </span>
                </div>


                <h1 className="qb-title">
               {resolvedSubjectName.split(" ").slice(0, -1).join(" ") || resolvedSubjectName} <span>{resolvedSubjectName.split(" ").slice(-1).join(" ")}</span>
                </h1>

                <p className="qb-sub qbb-list">Practice more , score higher !</p>


              </div>
            </div>
          </div>
       
         <div className="d-flex d-xl-block align-items-center mobile-filter-box">
            <label className="qbl-search" >
              <i className="bi bi-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                disabled={isQuestionListPending}
              />
            </label> 
              
              <button
                type="button"
                className="mobile-Filter-icon d-flex d-lg-flex d-xl-none"
                onClick={openFilters}
                disabled={isQuestionListPending}
                aria-label="Open filters"
                aria-expanded={isFilterOpen}
                aria-controls="qbank-filter-drawer"
              >
                <span className="filter-card__icon">
                   <i className="bi bi-sliders2"></i>
                </span>
              </button>
            </div>

            {isQuestionListPending ? (
              <SummaryRowShimmer />
            ) : (
              <div className="qbl-summaryRow">
                <h2>{`${Number(questionData?.totalResults || 0)} Result Found`}</h2>
                {guessScoreQuery.isLoading ? (
                  <span
                    className="dashboard-skeleton dashboard-skeleton--button qbl-summaryRow__shimmer-guess"
                    aria-hidden="true"
                  />
                ) : (
                  <button
                    type="button"
                    className="qbl-summaryRow__guess"
                    onClick={() => setIsGuessScoreOpen(true)}
                  >
                    {`Guess Score: ${guessScoreLabel}`}
                  </button>
                )}
              </div>
            )}

            <QuickActionCard />

            <div className="qbl-questionList" aria-label="Question list">
              {isQuestionListPending && !mergedQuestions.length ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <QuestionCardShimmer key={index} />
                ))
              ) : null}
              {!isQuestionListPending && loadError ? (
                <div className="choose-state__status choose-state__status--error">{loadError}</div>
              ) : null}
              {!isQuestionListPending && !loadError && !mergedQuestions.length ? (
                <div className="choose-state__status">No questions available.</div>
              ) : null}
              {!loadError
                ? mergedQuestions.map((item) => (
                    <QuestionCard
                      key={item.id}
                      item={item}
                      onOpen={handleOpenQuestion}
                      onBookmark={handleBookmarkToggle}
                      onLike={handleLikeToggle}
                      onView={handleOpenViewModal}
                      onReport={handleOpenReportModal}
                      onNotes={handleOpenNotesModal}
                      isBookmarkUpdating={
                        toggleBookmark.isPending &&
                        Number(toggleBookmark.variables?.question_id) === Number(item.id)
                      }
                      isLikeUpdating={
                        toggleLike.isPending &&
                        Number(toggleLike.variables?.question_id) === Number(item.id)
                      }
                    />
                  ))
                : null}
              {isQuestionListPending && mergedQuestions.length ? <QuestionCardShimmer /> : null}
            </div>
            {!isQuestionListPending && !loadError && Number(questionData?.pagination?.totalPages || 1) > 1 ? (
              <div className="qbl-pagination">
                <button
                  type="button"
                  className="qbl-pagination__nav"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((previousPage) => Math.max(1, previousPage - 1))}
                >
                  Previous
                </button>
                <div className="qbl-pagination__pages">
                  {visiblePages.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`qbl-pagination__page${pageNumber === currentPage ? " is-active" : ""}`}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="qbl-pagination__nav"
                  disabled={currentPage >= Number(questionData?.pagination?.totalPages || 1)}
                  onClick={() =>
                    setCurrentPage((previousPage) =>
                      Math.min(Number(questionData?.pagination?.totalPages || 1), previousPage + 1)
                    )
                  }
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>
        </div>

        <div
          className={`qbl-filterBackdrop ${isFilterOpen ? "is-open" : ""}`}
          onClick={closeFilters}
          aria-hidden="true"
        />

        <div
          id="qbank-filter-drawer"
          className={`qbl-filterPanel ${isFilterOpen ? "is-open" : ""}`}
        >
          <QBankFilter
            className={isFilterOpen ? "is-open" : ""}
            onClose={closeFilters}
            onApply={handleApplyFilters}
            appliedFilters={appliedFilters}
            queryParams={filterQueryParams}
          />
        </div>

        <ViewModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          question={selectedQuestion}
        />

        <ReportModal
          open={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          question={selectedQuestion}
          questionQueryParams={questionQueryParams}
        />

        <StickyNotesPopup
          open={isNotesModalOpen}
          onClose={() => setIsNotesModalOpen(false)}
          question={selectedQuestion}
          questionQueryParams={questionQueryParams}
        />

        <GuessScoreModal
          open={isGuessScoreOpen}
          onClose={() => setIsGuessScoreOpen(false)}
          activeView={guessScoreView}
          onToggleView={() =>
            setGuessScoreView((currentView) => (currentView === "subject" ? "all" : "subject"))
          }
          subjectTitle={guessScoreTitle}
          subjectSubtitle={guessScoreSubtitle}
          subjectSummary={guessScoreQuery.data}
          isSubjectLoading={guessScoreQuery.isLoading}
          allSubjectsSummary={allSubjectsGuessScoreQuery.data}
          isAllSubjectsLoading={allSubjectsGuessScoreQuery.isLoading}
        />
      </div>
    </HomeDashboardLayout>
    </>
  );
};

export default QuestionBankList;








